import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { Game, GameTag } from '@app/shared/models/game.model';
import { environment } from '@environments/environment';
import { GameOrder } from '../models/lobby.model';
import { ModalConfig, Prize } from '../models/modal.model';
import { TagsService } from './tags.service';
import { TagsRepository } from '../state/tags/tags.repository';

@Injectable({
  providedIn: 'root',
})
export class LobbyService {
  private http = inject(HttpClient);
  private tagsService = inject(TagsService);
  private tagsRepo = inject(TagsRepository);

  private gameDiscontinuedRefundedSource = new Subject<Game>();
  private apiServiceUrl = `${environment.apiUrl}/api/notifications`;

  gameDiscontinuedRefunded$ = this.gameDiscontinuedRefundedSource.asObservable();
  games: Game[] = [];

  getLobbyGames(): Observable<Game[]> {
    return this.http.get<Game[]>(`${environment.apiUrl}/api/games/get-lobby`);
  }

  notifyRefundedDiscontinuedGame(game: Game) {
    this.gameDiscontinuedRefundedSource.next(game);
  }

  getUserNotifications(promoCode: string): Observable<ModalConfig[]> {
    const options = promoCode ? { params: new HttpParams().set('promoCode', promoCode) } : {};

    return this.http.get<ModalConfig[]>(`${this.apiServiceUrl}/getUserNotifications`, options);
  }

  deleteUserNotification(notificationId: string): Observable<Prize> {
    const options = {
      params: new HttpParams().set('notificationId', notificationId),
    };
    return this.http.delete<Prize>(`${this.apiServiceUrl}/resolveNotification`, options);
  }

  getGamesOrdered(order: GameOrder, games: Game[]): Game[] {
    switch (order) {
      case GameOrder.ONBOARDING:
        return games;
      case GameOrder.DEFAULT:
        return this.sortByTierFeaturedFirst(games);
      case GameOrder.LASTPLAY:
        return this.sortByLastPlayed(games);
      case GameOrder.FEATURED:
        return this.filterByFeatured(games);
      case GameOrder.TROPHIES:
        return this.sortByTrophies(games);
      case GameOrder.BAR:
        return this.filterByBarGames(games);
      default:
        return this.sortByTierFeaturedFirst(games);
    }
  }

  private sortByTierFeaturedFirst(games: Game[]): Game[] {
    return games
      .filter((game: Game) => game.gameId)
      .sort((a: Game, b: Game) => {
        const featuredOrder = this.sortByFeaturedPriority(a, b);
        const tierOrder = (b.trophy?.tier ?? 0) - (a.trophy?.tier ?? 0);

        if (featuredOrder !== 0) return featuredOrder;

        if (tierOrder !== 0) return tierOrder;

        return 0;
      });
  }

  private sortByLastPlayed(games: Game[]): Game[] {
    return games
      .filter((game: Game) => game.lastPlayedOrder >= 0)
      .sort((a: Game, b: Game) => {
        if (a.lastPlayedOrder === b.lastPlayedOrder) {
          return 0;
        } else {
          return a.lastPlayedOrder - b.lastPlayedOrder;
        }
      })
      .slice(0, 50);
  }

  private filterByFeatured(games: Game[]): Game[] {
    const maintenance = this.tagsService.getMaintenanceTag().name;
    const locked = this.tagsService.getLockedTag().name;

    return games
      .filter((game: Game) => game.tags.some((tag) => tag.name !== maintenance && tag.name !== locked))
      .sort((a, b) => this.sortByFeaturedPriority(a, b));
  }

  private sortByTrophies(games: Game[]): Game[] {
    return games
      .filter(
        (game) => (game.isUnlocked || this.gameOpenedByTag(game)) && game.trophy && !game.trophy?.finalTrophyCompleted
      )
      .sort((a: Game, b: Game) => {
        const order = a.trophy?.total - a.trophy?.current - (b.trophy?.total - b.trophy?.current);

        return order !== 0 ? order : -1;
      });
  }

  private filterByBarGames(games: Game[]): Game[] {
    return games
      .filter((game: Game) => game.isBarGame)
      .sort((a: Game, b: Game) => {
        const featuredOrder = this.sortByFeaturedPriority(a, b);
        const tierOrder = (b.trophy?.tier ?? 0) - (a.trophy?.tier ?? 0);

        if (featuredOrder !== 0) return featuredOrder;

        if (tierOrder !== 0) return tierOrder;

        return 0;
      });
  }

  private sortByFeaturedPriority(a: Game, b: Game): number {
    const gameTagPriorityA = this.getTagPriority(a.tags);
    const gameTagPriorityB = this.getTagPriority(b.tags);

    if (gameTagPriorityA > gameTagPriorityB) return -1;
    if (gameTagPriorityA < gameTagPriorityB) return 1;

    return 0;
  }

  private getTagPriority(gameTags: GameTag[]): number {
    if (gameTags.length) {
      return this.tagsRepo.getAllTags().find((tag) => tag.name === gameTags[0].name).priority;
    } else {
      return 0;
    }
  }

  private gameOpenedByTag(game: Game): boolean {
    if (game.tags.length) {
      return this.tagsRepo.getAllTags().find((tag) => tag.name === game.tags[0].name).isGameOpen ? true : false;
    } else {
      return false;
    }
  }
}
